import { useMemo } from 'react'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import isNil from 'lodash/isNil'
import { useLifetimeTokens } from 'state/global/hooks'
import { useGetLicensesQuery } from 'state/services/indexNode/indexNode'

/**
 * The name the holder gave each licence, keyed by its token id.
 *
 * The pickers that choose a licence — transferring one, listing one for sale
 * — read the wallet's tokens from the collection itself, and a token id is a
 * unix timestamp: nothing a person recognises. This is what lets those lists
 * say "Sam07" instead of "#1739274433", which is the only name the holder
 * ever gave it.
 *
 * Empty until the licence session exists, and empty for anyone who has not
 * named their licences. Callers fall back to the token id, which is always
 * true even when it is unfriendly.
 */
export const useLicenceNamesByToken = (): Record<string, string> => {
  const { account } = useWeb3React()
  const lifetimeTokens = useLifetimeTokens()
  const accessToken = lifetimeTokens?.[account]

  const { data } = useGetLicensesQuery(
    { account, accessToken },
    { skip: isNil(accessToken) || !account }
  )

  return useMemo(() => {
    const names: Record<string, string> = {}
    for (const licence of data ?? []) {
      // A licence with no token cannot appear in a picker, and one with no
      // name has nothing better to offer than its id.
      if (licence.tokenId && licence.name) names[licence.tokenId] = licence.name
    }
    return names
  }, [data])
}
